import React, { useEffect, useState } from 'react';
import { useLang } from '../../lib/lang';

/**
 * GDPR Cookie Consent Banner.
 *
 * Sticky bottom banner shown until the user picks a consent option.
 * Choices are stored in localStorage as:
 *   araia_cookie_consent = {
 *     technical:  true,          // always on — required for the site to work
 *     analytics:  bool,
 *     timestamp:  ISO string,
 *   }
 * Validity is 12 months; after that we prompt again.
 *
 * Public callable API:
 *   window.__araia.getConsent() → parsed object or null
 * This makes it easy for analytics scripts to gate themselves.
 */

const STORAGE_KEY = 'araia_cookie_consent';
const VALIDITY_MS = 365 * 24 * 60 * 60 * 1000; // 12 months

function readStoredConsent() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return null;
        const ts = new Date(parsed.timestamp).getTime();
        if (isNaN(ts) || Date.now() - ts > VALIDITY_MS) return null;
        return parsed;
    } catch (e) {
        return null;
    }
}

function writeConsent(analytics) {
    const payload = {
        technical: true,
        analytics: !!analytics,
        timestamp: new Date().toISOString(),
    };
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (e) {
        /* noop */
    }
    // Global hook so analytics/tracking scripts can react without polling.
    if (typeof window !== 'undefined') {
        window.__araia = window.__araia || {};
        window.__araia.consent = payload;
        window.dispatchEvent(new CustomEvent('araia:consent-change', { detail: payload }));
    }
    return payload;
}

// Expose a read-only getter globally on first import.
if (typeof window !== 'undefined') {
    window.__araia = window.__araia || {};
    window.__araia.getConsent = readStoredConsent;
}

const T = {
    it: {
        body: 'Utilizziamo cookie tecnici e di analisi per migliorare la tua esperienza.',
        acceptAll: 'Accetta tutti',
        onlyTechnical: 'Solo tecnici',
        customize: 'Personalizza',
        save: 'Salva preferenze',
        cancel: 'Annulla',
        panelTitle: 'Preferenze cookie',
        technicalLabel: 'Tecnici (obbligatori)',
        analyticsLabel: 'Analytics',
        privacyLink: 'Privacy Policy',
        cookieLink: 'Cookie Policy',
    },
    en: {
        body: 'We use technical and analytics cookies to improve your experience.',
        acceptAll: 'Accept all',
        onlyTechnical: 'Only technical',
        customize: 'Customize',
        save: 'Save preferences',
        cancel: 'Cancel',
        panelTitle: 'Cookie preferences',
        technicalLabel: 'Technical (required)',
        analyticsLabel: 'Analytics',
        privacyLink: 'Privacy Policy',
        cookieLink: 'Cookie Policy',
    },
};

export default function CookieBanner() {
    const lang = useLang();
    const t = T[lang] || T.it;
    const [shouldShow, setShouldShow] = useState(false);
    const [showPanel, setShowPanel] = useState(false);
    const [analyticsOn, setAnalyticsOn] = useState(false);

    useEffect(() => {
        // Show only if no valid stored consent
        if (readStoredConsent() !== null) return;

        // iter74 · Coordinate with the cinematic intro on Home so the banner
        // never overlaps or pre-empts the splash sequence.
        //   - if the intro flag is already set OR no intro stage is mounted
        //     → show after a short delay (~500ms) so the reveal isn't
        //       aggressive on the first paint
        //   - otherwise wait for the `araia:intro-complete` event fired by
        //     Home.jsx and add a 1s breather so the split-screen has time
        //     to settle before the banner slides in.
        let cleanupTimer = null;
        let cleanupListener = null;

        const scheduleShow = (delayMs) => {
            cleanupTimer = setTimeout(() => setShouldShow(true), delayMs);
        };

        const introFlag = (() => {
            try { return sessionStorage.getItem('araia_intro_shown') === '1'; }
            catch (e) { return true; }  // assume no intro if sessionStorage blocked
        })();
        const introInDom = typeof document !== 'undefined'
            && document.querySelector('[data-testid="intro-stage"]') !== null;

        if (introFlag || !introInDom) {
            scheduleShow(500);
        } else {
            const onIntroDone = () => scheduleShow(1000);
            window.addEventListener('araia:intro-complete', onIntroDone, { once: true });
            cleanupListener = () => window.removeEventListener('araia:intro-complete', onIntroDone);
        }

        return () => {
            if (cleanupTimer) clearTimeout(cleanupTimer);
            if (cleanupListener) cleanupListener();
        };
    }, []);

    if (!shouldShow) return null;

    const finish = (analytics) => {
        writeConsent(analytics);
        setShouldShow(false);
    };

    return (
        <div
            className="cookie-banner"
            data-testid="cookie-banner"
            role="dialog"
            aria-label={t.panelTitle}
            aria-modal="false"
        >
            {!showPanel ? (
                <>
                    <p className="cookie-banner__text">
                        {t.body}{' '}
                        <a href={`/${lang}/legal/privacy-policy`} className="cookie-banner__link" data-testid="cookie-privacy-link">
                            {t.privacyLink}
                        </a>{' · '}
                        <a href={`/${lang}/legal/cookie-policy`} className="cookie-banner__link" data-testid="cookie-cookie-link">
                            {t.cookieLink}
                        </a>
                    </p>
                    <div className="cookie-banner__actions">
                        <button
                            type="button"
                            className="cookie-btn cookie-btn--primary"
                            onClick={() => finish(true)}
                            data-testid="cookie-accept-all"
                        >
                            {t.acceptAll}
                        </button>
                        <button
                            type="button"
                            className="cookie-btn cookie-btn--ghost"
                            onClick={() => finish(false)}
                            data-testid="cookie-only-technical"
                        >
                            {t.onlyTechnical}
                        </button>
                        <button
                            type="button"
                            className="cookie-btn cookie-btn--ghost"
                            onClick={() => setShowPanel(true)}
                            data-testid="cookie-customize"
                        >
                            {t.customize}
                        </button>
                    </div>
                </>
            ) : (
                <div className="cookie-panel" data-testid="cookie-panel">
                    <div className="cookie-panel__title">{t.panelTitle}</div>
                    <label className="cookie-toggle cookie-toggle--disabled">
                        <input type="checkbox" checked disabled data-testid="cookie-toggle-technical" />
                        <span>{t.technicalLabel}</span>
                    </label>
                    <label className="cookie-toggle">
                        <input
                            type="checkbox"
                            checked={analyticsOn}
                            onChange={(e) => setAnalyticsOn(e.target.checked)}
                            data-testid="cookie-toggle-analytics"
                        />
                        <span>{t.analyticsLabel}</span>
                    </label>
                    <div className="cookie-banner__actions">
                        <button
                            type="button"
                            className="cookie-btn cookie-btn--primary"
                            onClick={() => finish(analyticsOn)}
                            data-testid="cookie-save"
                        >
                            {t.save}
                        </button>
                        <button
                            type="button"
                            className="cookie-btn cookie-btn--ghost"
                            onClick={() => setShowPanel(false)}
                            data-testid="cookie-cancel"
                        >
                            {t.cancel}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
